import { FC, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, Button, Grid, Paper, Typography } from '@mui/material'
import { Checkbox, FormGroup, FormControlLabel, FormLabel } from '@mui/material'
import {
  useClientApiCall,
  useClientApiMutation,
} from '../../hooks/useClientApiCall'
import StoreIcon from '@mui/icons-material/Store'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import { useSearchParams } from 'react-router-dom/dist'
import { ApplicationLoadingScreen } from '../../components/ApplicationLoadingScreen'
import { AppplicationErrorScreen } from '../../components/ApplicationErrorScreen'

export const CloverPOS: FC = () => {
  const [params] = useSearchParams()
  const connection = useClientApiCall<any>(params.get('session')!, 'connection')

  if (connection.isLoading) return <ApplicationLoadingScreen />
  if (connection.isError) return <AppplicationErrorScreen />

  return <CloverView connection={connection} />
}

export const CloverView: FC<{ connection: any }> = ({ connection }) => {
  const [params] = useSearchParams()
  const [syncingCatalog, setSyncingCatalog] = useState(false)
  const [disconnected, setDisconnected] = useState(false)
  const [autoPrintOptions, setAutoPrintOptions] = useState({
    card: false,
    cash: false,
  })
  useEffect(() => {
    if (connection?.data?.autoPrintOptions) {
      setAutoPrintOptions(connection.data.autoPrintOptions)
    }
  }, [connection?.data])

  const updateAutoPrintMutation = useClientApiMutation<any>(
    params.get('session')!,
    'enable-auto-print',
    'PATCH'
  )

  const handleToggle = (type: 'card' | 'cash') => (e: any) => {
    const updated = { ...autoPrintOptions, [type]: e.target.checked }
    console.log('Updated auto print options:', updated)
    setAutoPrintOptions(updated)

    updateAutoPrintMutation.mutate(
      { autoPrintOptions: updated },
      {
        onSuccess: () => {
          toast.success('Auto print settings updated')
        },
        onError: () => {
          toast.error('Failed to update auto print settings')
        },
      }
    )
  }

  const menuSyncMutation = useClientApiMutation<any>(
    params.get('session')!,
    'sync-catalog',
    'GET'
  )

  const cloverDisconnectMutation = useClientApiMutation<any>(
    params.get('session')!,
    'disconnect',
    'POST'
  )

  const doCatalogSync = () => {
    setSyncingCatalog(true)
    menuSyncMutation.mutate(undefined, {
      onSuccess: (data) => {
        setSyncingCatalog(false)
        window.close()
      },
      onError: (error) => {
        setSyncingCatalog(false)
        toast.error(`Catalog synced successfully`)
      },
    })
  }

  const onDisconnect = () => {
    setDisconnected(true)
    cloverDisconnectMutation.mutate(undefined, {
      onSuccess: (data) => {
        setDisconnected(false)
        window.close()
      },
      onError: (error) => {
        setDisconnected(false)
        toast.error(`Failed to disconnect`)
      },
    })
  }

  return (
    <Grid container spacing={0} sx={{ minHeight: '100vh' }}>
      {/* Left Image Section */}
      <Grid item sm={7} md={7} display={{ xs: 'none', sm: 'block' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            backgroundColor: '#f5f5f5',
          }}
        >
          <img
            src="https://static.bluelabellabs.com/wp-content/uploads/2019/06/Clover-App-and-Point-of-Sale.png"
            alt="Clover POS"
            style={{ width: '90%', maxWidth: 700, borderRadius: '12px' }}
          />
        </Box>
      </Grid>
      {/* Right Form Section */}
      <Grid
        item
        xs={12}
        sm={5}
        md={5}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#ffffff',
          padding: { xs: 3, sm: 6 },
        }}
      >
        <Box width="100%" maxWidth="500px">
          <Paper elevation={4} sx={{ p: 4, borderRadius: 4 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Clover Merchant Info
            </Typography>

            <Box display="flex" alignItems="center" mt={3}>
              <StoreIcon color="success" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Merchant Name
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {connection?.data?.locationName || 'N/A'}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" mt={3}>
              <VpnKeyIcon color="success" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Merchant ID
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  sx={{ wordBreak: 'break-word' }}
                >
                  {connection?.data?.cloverMerchantId || 'N/A'}
                </Typography>
              </Box>
            </Box>
            <Box mt={3}>
              <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                Enable Auto Print For Online Orders
              </FormLabel>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoPrintOptions.card}
                      onChange={handleToggle('card')}
                      disabled={updateAutoPrintMutation.isLoading}
                    />
                  }
                  label="Card"
                  sx={{ mr: 4 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoPrintOptions.cash}
                      onChange={handleToggle('cash')}
                      disabled={updateAutoPrintMutation.isLoading}
                    />
                  }
                  label="Cash"
                />
              </FormGroup>
            </Box>

            {/* {isConnected && ( */}
            <Box mt={5} display="flex" flexDirection="column" gap={2}>
              <Button
                variant="contained"
                onClick={doCatalogSync}
                color="success"
                disabled={syncingCatalog}
                fullWidth
                sx={{ height: 48 }}
              >
                {syncingCatalog ? 'Syncing...' : 'Sync Catalog'}
              </Button>
              <Button
                variant="outlined"
                onClick={onDisconnect}
                disabled={disconnected}
                color="error"
                fullWidth
                sx={{ height: 48 }}
              >
                {disconnected ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            </Box>
            {/* )} */}
          </Paper>
        </Box>
      </Grid>
    </Grid>
  )
}
